/**
 * The guard on the screens that show every brand at once.
 *
 * Three answers, not two, because a super admin in the admin view is not
 * locked out of anything: they are one click from the page. So they get the
 * switch right here instead of a refusal that sends them looking for it.
 * Everybody else, an ordinary admin included, is told whose page it is.
 *
 * The server refuses the same reads on the same terms (`requireSuperAdminView`),
 * so this guard decides what is drawn, not what may be read.
 */
import { Navigate, useLocation } from 'react-router-dom'
import { useSession, useViewSwitch, type GuardProps } from '../lib/session'
import { Button } from './Button'
import { useToast } from './Toast'

export function RequireSuperAdminView({ children, fallback = null }: Omit<GuardProps, 'denied'>): JSX.Element {
  const { isLoading, isSignedOut, session, isSuperAdmin, superAdminView } = useSession()
  const location = useLocation()

  if (isLoading) return <>{fallback}</>
  if (isSignedOut || !session) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }
  if (superAdminView) return <>{children}</>
  if (isSuperAdmin) return <AdminViewNotice />
  return <SuperAdminOnlyNotice />
}

function AdminViewNotice(): JSX.Element {
  const change = useViewSwitch()
  const toast = useToast()
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <h1 className="text-xl font-semibold text-ink">You are in the admin view</h1>
      <p className="mt-2 text-sm text-ink-muted">
        This page shows every brand at once, which is part of the super admin view. Switch it on to see the page. The
        switch is also at the bottom of the sidebar, above your name.
      </p>
      <div className="mt-5 flex justify-center">
        <Button
          loading={change.isPending}
          onClick={() =>
            change.mutate('super_admin', {
              onError: (error) => toast.error('Could not switch the view', error),
            })
          }
        >
          Switch to the super admin view
        </Button>
      </div>
    </div>
  )
}

function SuperAdminOnlyNotice(): JSX.Element {
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <h1 className="text-xl font-semibold text-ink">This page is for super admins</h1>
      <p className="mt-2 text-sm text-ink-muted">
        It shows every brand at once. Inside one brand you still see everything your role allows, from the brand
        switcher at the top of the sidebar.
      </p>
    </div>
  )
}
